import { useState, useRef, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Star, Heart, Sparkles } from 'lucide-react';
import { BIG_QUESTION } from '@/config/valentineContent';

const RESPONSES = BIG_QUESTION.starResponses;

export default function BigQuestion() {
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [floatingHearts, setFloatingHearts] = useState([]);
  const heartIdRef = useRef(0);

  useEffect(() => {
    if (submitted) return;
    const interval = setInterval(() => {
      const id = ++heartIdRef.current;
      setFloatingHearts(prev => [...prev.slice(-6), {
        id,
        x: 10 + Math.random() * 80,
        size: 8 + Math.random() * 10,
        duration: 3 + Math.random() * 2,
        delay: Math.random() * 0.5,
      }]);
      setTimeout(() => setFloatingHearts(prev => prev.filter(h => h.id !== id)), 5500);
    }, 1200);
    return () => clearInterval(interval);
  }, [submitted]);

  const handleSelect = (rating) => {
    setSelectedRating(rating);
    setSubmitted(true);

    if (rating >= 4) {
      const colors = ['#E91E63', '#F48FB1', '#FF80AB', '#FCE4EC', '#CE93D8', '#fff', '#F06292'];
      const end = Date.now() + 3000;
      const frame = () => {
        confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors });
        confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  };

  const activeFill = hoveredStar || selectedRating;
  const response = RESPONSES[selectedRating];

  return (
    <section id="big-question" data-testid="big-question-section" className="section-romantic text-center relative overflow-hidden">
      {floatingHearts.map(h => (
        <div key={h.id} className="bq-float-heart" style={{ left: `${h.x}%`, fontSize: h.size, animationDuration: `${h.duration}s`, animationDelay: `${h.delay}s`, color: 'var(--accent-light)' }}>
          <Heart size={h.size} fill="currentColor" strokeWidth={0} />
        </div>
      ))}

      <div className="max-w-lg mx-auto relative z-10">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-12 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, var(--accent-light))' }} />
          <Sparkles size={14} style={{ color: 'var(--accent-primary)' }} />
          <div className="w-12 h-[1px]" style={{ background: 'linear-gradient(90deg, var(--accent-light), transparent)' }} />
        </div>

        <p className="font-body text-[10px] tracking-widest uppercase mb-2" style={{ color: 'var(--accent-light)' }}>
          One last thing…
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-3 bq-question-text" style={{ color: 'var(--text-primary)' }}>
          Rate Your Birthday
        </h2>
        <p className="font-body text-xs mb-10" style={{ color: 'var(--text-secondary)' }}>
          (Be honest… but also be nice to me)
        </p>

        {/* Stars */}
        <div className="flex items-center justify-center gap-2 sm:gap-3 mb-8">
          {[1, 2, 3, 4, 5].map(star => {
            const isFilled = star <= activeFill;
            return (
              <button
                key={star}
                disabled={submitted}
                onMouseEnter={() => !submitted && setHoveredStar(star)}
                onMouseLeave={() => !submitted && setHoveredStar(0)}
                onClick={() => !submitted && handleSelect(star)}
                className="birthday-star-btn group"
                style={{ cursor: submitted ? 'default' : 'pointer', animationDelay: `${star * 80}ms` }}
              >
                <Star
                  size={42}
                  fill={isFilled ? 'var(--accent-primary)' : 'transparent'}
                  stroke={isFilled ? 'var(--accent-primary)' : 'var(--accent-light)'}
                  strokeWidth={1.5}
                  className="transition-all duration-200"
                  style={{
                    filter: isFilled ? 'drop-shadow(0 0 8px var(--accent-glow))' : 'none',
                    transform: isFilled ? 'scale(1.15)' : 'scale(1)',
                  }}
                />
                {isFilled && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="birthday-star-glow" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Hover hint */}
        {!submitted && hoveredStar === 0 && (
          <p className="font-body text-[10px] bq-hint-text" style={{ color: 'var(--accent-light)' }}>
            Tap a star to rate
          </p>
        )}

        {/* Hover preview */}
        {!submitted && hoveredStar > 0 && (
          <p className="font-body text-sm bq-hint-text" style={{ color: 'var(--text-secondary)' }}>
            {hoveredStar === 5 ? "Perfect!" : hoveredStar === 4 ? "Almost perfect!" : hoveredStar === 3 ? "Just okay?" : hoveredStar === 2 ? "Really?" : "Ouch..."}
          </p>
        )}

        {/* Response after selection */}
        {submitted && response && (
          <div className="bq-dialog rounded-2xl p-6 sm:p-8 mx-auto max-w-sm relative overflow-hidden"
            style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--accent-glow)', boxShadow: '0 8px 40px var(--accent-glow)' }}>
            <span className="text-4xl block mb-3">{response.emoji}</span>
            <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              {response.text}
            </p>
            {selectedRating === 5 && (
              <div className="mt-4">
                <div className="bq-celebration-heart inline-block">
                  <Heart size={24} fill="var(--accent-primary)" strokeWidth={0} />
                </div>
                <p className="font-cursive text-2xl mt-2" style={{ color: 'var(--accent-primary)' }}>
                  I love you
                </p>
              </div>
            )}
            {selectedRating < 5 && (
              <button
                onClick={() => { setSubmitted(false); setSelectedRating(0); setHoveredStar(0); }}
                className="mt-4 px-5 py-2 rounded-xl font-body text-xs transition-all duration-200 hover:scale-105 active:scale-95"
                style={{ color: 'var(--accent-primary)', border: '1px solid var(--accent-glow)', background: 'none', cursor: 'pointer' }}
              >
                Let me reconsider…
              </button>
            )}
            <div className="absolute top-3 left-3 opacity-20" style={{ color: 'var(--accent-light)' }}><Sparkles size={12} /></div>
            <div className="absolute bottom-3 right-3 opacity-20" style={{ color: 'var(--accent-light)' }}><Sparkles size={12} /></div>
          </div>
        )}
      </div>
    </section>
  );
}
